<template>
  <b-container fluid>
    <page-header title="User List">
      <template #actions>
        <button-options :access="'master_user'" :hideAdd="false" :hideDetail="true" :isRefreshEnabled="true"
          :isRefreshed="isRefreshed" @onAddItemClicked="openAddForm" @onRefreshed="getData('Refreshed')" />
      </template>
    </page-header>
    <div class="iq-card px-3">
      <div class="iq-card-body">
        <b-row class="mb-3">
          <b-col md="4" sm="6">
            <input class="form-control" type="text" placeholder="Search by email" v-model="searchTerm"
              @input="debouncedSearch">
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <user-list :access="'master_user'" :data="data" :loading="isLoading" :pagination="pagination"
              @onActionDelete="openDelete" @onPageChanged="onPageChanged" @update="onUpdated" />
          </b-col>
        </b-row>
      </div>
      <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayForm">
        <user-form :key="formKey" :data="currentUser" :errors="errors" :isSubmit="isSubmit"
          @onSubmitClicked="onSubmitClicked" @onCancelAddClicked="onCancel" />
      </b-modal>
      <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayDeletion">
        <delete-item :isSubmit="isSubmit" @onCancelDeletion="displayDeletion = false"
          @onConfirmDeletion="onConfirmDeletion" />
      </b-modal>
    </div>
  </b-container>
</template>

<script>

import DeleteItem from '@/components/directives/delete-item';
import ButtonOptions from '@/components/directives/button-options';
import UserList from '@/components/userrole/user/List';
import UserForm from '@/components/userrole/user/Form';
import UserSvc from '@/services/UserSvc';

import requestBuilder from '@/util/master/request.builder';
import { isCancelled, isAuthError, requestErrorMessage } from '@/util/master/requestError';

// ponytail: single bulk fetch of all logins, switch to a real batch-by-id endpoint if user count grows past this
const LOGIN_FETCH_MAX = 1000;

export default {
  name: 'User',
  components: {
    DeleteItem, UserForm, UserList, ButtonOptions,
  },
  data() {
    return {
      currentUser: {},
      data: [],
      displayDeletion: false,
      displayForm: false,
      errors: {},
      formKey: 0,
      filter: {
        userId: null,
      },
      isLoading: false,
      isRefreshed: false,
      isSubmit: false,
      pagination: {
        current: 1,
        pageSize: 8,
        totalSize: 0,
      },
      searchTerm: '',
    };
  },
  methods: {
    async deleteData(notification, data) {
      this.isSubmit = true;
      try {
        const userRes = await UserSvc.delete(data.userId);
        const usloRes = await UserSvc.deleteUserLogin(data.userId);
        if (userRes.data.status.error === 0 && usloRes.data.status.error === 0) {
          await this.$toast.success(notification);
          this.displayDeletion = false;
          this.pagination.current = 1;
          this.getData();
        } else {
          this.$toast.error(userRes.data.status.message);
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.isSubmit = false;
      }
    },
    async getData(notification) {
      try {
        this.isLoading = true;
        this.isRefreshed = true;
        const resp = await UserSvc.getUsers({
          params: {
            ...requestBuilder.buildFilters(this.pagination, this.filter),
          },
        });
        const userLoginRes = await UserSvc.getUserLogin({ params: { page: 1, max: LOGIN_FETCH_MAX } });
        const usloUserNameByUserId = new Map(
          (userLoginRes.data.data || []).map((uslo) => [uslo.usloUserId, uslo.usloUserName]),
        );

        this.data = (resp.data.data || []).map((user) => ({
          ...user,
          usloUserName: usloUserNameByUserId.get(user.userId) || '-',
        }));

        this.pagination.totalSize = resp.data.pagination?.totalData || 0;
        if (notification) {
          this.$toast.success(notification);
        }
      } catch (e) {
        if (isCancelled(e)) return;
        if (isAuthError(e)) this.doLogOut();
        else this.$toast.error(requestErrorMessage(e));
      } finally {
        this.isLoading = false;
        this.isRefreshed = false;
      }
    },
    onCancel() {
      this.currentUser = {};
      this.displayForm = false;
    },
    onConfirmDeletion() {
      this.deleteData('Deleted complete', this.selected);
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getData();
    },
    onUpdated() {
      this.getData('Updated');
    },
    async onSubmitClicked(form) {
      this.errors = {};
      this.isSubmit = true;
      try {
        const userRes = await UserSvc.create({ userId: form.userId, userStatus: form.userStatus });
        if (userRes.data.status.error !== 0) {
          const errors = userRes.data.status.message;
          this.$toast.error(typeof errors === 'object' ? 'Error found' : errors);
          return;
        }

        const activeCompany = this.$store.getters['auth/activeCompany'];
        const uscoRes = await UserSvc.addUserCompany({
          uscoUserId: form.userId,
          uscoCompId: activeCompany.compId,
          uscoAllAssets: form.uscoAllAssets,
        });
        if (uscoRes.data.status.error !== 0) {
          this.$toast.error(`User created, but failed to link company: ${uscoRes.data.status.message}. Finish it manually on the Detail page.`);
          this.goToUserDetail(form.userId);
          return;
        }

        const usloRes = await UserSvc.createUserLogin({
          usloUserId: form.userId,
          usloUserName: form.usloUserName,
          usloPassword: form.usloPassword,
        });
        if (usloRes.data.status.error !== 0) {
          this.$toast.error(`User & company created, but failed to create login: ${usloRes.data.status.message}. Finish it manually on the Detail page.`);
          this.goToUserDetail(form.userId);
          return;
        }

        const usroRes = await UserSvc.addUserRole({ usroRoleId: form.roleId, usroUserId: form.userId });
        if (usroRes.data.status.error !== 0) {
          this.$toast.error(`User, company & login created, but failed to assign role: ${usroRes.data.status.message}. Finish it manually on the Detail page.`);
          this.goToUserDetail(form.userId);
          return;
        }

        const failedAssets = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const massId of form.assetIds) {
          // eslint-disable-next-line no-await-in-loop
          const uassRes = await UserSvc.createUserAsset({ uassMassId: massId, uassUserId: form.userId });
          if (uassRes.data.status.error !== 0) failedAssets.push(massId);
        }
        if (failedAssets.length > 0) {
          this.$toast.error(`Failed to assign ${failedAssets.length} vessel(s). Finish it manually on the Detail page.`);
          this.goToUserDetail(form.userId);
          return;
        }

        await this.getData('User created successfully');
        this.currentUser = {};
        this.displayForm = false;
      } catch (e) {
        this.$toast.error(e.response?.data?.status?.message || 'Error while submitting');
      } finally {
        this.isSubmit = false;
      }
    },
    goToUserDetail(userId) {
      this.displayForm = false;
      this.$router.push({ name: 'UserDetail', params: { id: userId } });
    },
    openAddForm() {
      this.currentUser = { uscoAllAssets: '0' };
      this.formKey += 1;
      this.displayForm = true;
    },
    openDelete(data) {
      this.displayDeletion = true;
      this.selected = data;
    },
    doLogOut() {
      this.$toast.error('Please re-login');
      this.$store.dispatch('auth/logout');
      this.$router.replace({ name: 'Login' });
    },
  },
  created() {
    this.debouncedSearch = this._.debounce(() => {
      this.filter.userId = this.searchTerm.trim() || null;
      this.pagination.current = 1;
      this.getData();
    }, 400);
  },
  mounted() {
    this.getData();
  },
};
</script>

<style scoped></style>
